import { FREE_SUBSCRIPTION_APPS, Practice, SubscriptionType } from '@cc/schema';
import { useGetIdentity } from '@refinedev/core';
import { useQueryClient } from '@tanstack/react-query';
import { add, parseISO } from 'date-fns';
import { useMemo } from 'react';
import { IUser } from '../providers';
import { Client } from '../schema/be-graphql-generated';

export const useSubscriptionWarnings = () => {
  const queryClient = useQueryClient();

  const { data: identity, remove, refetch } = useGetIdentity<IUser>();

  const clientsData = queryClient.getQueriesData({
    queryKey: ['default', 'clients', 'list'],
  });

  const subscription = (
    queryClient.getQueriesData({
      queryKey: ['getUserIdentity'],
    })?.[0]?.[1] as any
  )?.db?.practice?.subscription as Practice['subscription'];

  /**
   * The FULLEST cached page of the roster, not the first one cached (R108, the founder's prod pass
   * 2026-09-23). The Clients page loads 10 rows, then re-pages to `clientsPurchased × 4`; both
   * queries stay in the cache and `[0]` was the 10-row one, so a practice with more clients than
   * a page counted at most 10 seats and never saw its banner (his 42 / 50 surfaced only once the
   * log's stored count existed). A practice whose billing is the log's reads `materialised` below.
   */
  const fullestClientsPage = clientsData
    .map(([, page]) => page as any)
    .reduce<any>(
      (best, page) =>
        (page?.data?.data?.length ?? 0) > (best?.data?.data?.length ?? 0)
          ? page
          : best,
      undefined,
    );

  const clientsAdded = fullestClientsPage?.total || 0;

  const clientsPremiumViaPractice = (
    (fullestClientsPage?.data?.data as Client[]) || []
  ).filter((u) =>
    u.activeSubscriptions?.data?.some(
      (s) =>
        s?.type === SubscriptionType.PROVIDER &&
        s.practiceId === identity?.db?.practiceId,
    ),
  );

  /**
   * E25 · the MATERIALISED warnings (F-b13, founder 2026-09-21: "Yes"): a practice whose billing
   * is the log's carries `subscriptionWarnings` on its row — evaluated after every Stripe fact
   * and, since the same day, after every SEAT change (the runner's
   * `e25.reevaluateWarningsOnSeatChange`). When they are there, they ARE the banner; the
   * day-based fields are derived at read from the stored expiration date (`warningsAt` in the
   * domain, restated here). A practice not yet across carries none and keeps the compute below.
   */
  const materialised = (
    queryClient.getQueriesData({
      queryKey: ['getUserIdentity'],
    })?.[0]?.[1] as any
  )?.db?.practice?.subscriptionWarnings as
    | {
        subscriptionExpired: boolean;
        subscriptionExpirationDate?: string | null;
        clientsPurchased: number;
        clientsAdded: number;
        limitReached: boolean;
        limitApproaching: boolean;
        clientsLeft: number;
        evaluatedAt: string;
      }
    | null
    | undefined;

  const stringifiedSubscrition = JSON.stringify(subscription);
  const stringifiedMaterialised = JSON.stringify(materialised ?? null);

  const res = useMemo(() => {
    if (materialised) {
      const expirationDate = materialised.subscriptionExpirationDate
        ? parseISO(materialised.subscriptionExpirationDate)
        : null;
      const daysTillSubscriptionExpires = expirationDate
        ? Math.floor(
            (expirationDate.getTime() - Date.now()) / (1000 * 60 * 60 * 24),
          )
        : 0;
      return {
        subscriptionExpired: materialised.subscriptionExpired,
        subscriptionExpirationDate: expirationDate,
        daysTillSubscriptionExpires,
        subscriptionExpiringInDays:
          daysTillSubscriptionExpires > 0 && daysTillSubscriptionExpires < 7
            ? daysTillSubscriptionExpires
            : 0,
        clientsPurchased: materialised.clientsPurchased,
        clientsAdded: materialised.clientsAdded,
        limitReached: materialised.limitReached,
        limitApproaching: materialised.limitApproaching,
        clientsLeft: materialised.clientsLeft,
      };
    }

    const subscriptionExpirationDate = ['active', 'trialing'].includes(
      subscription?.status || '',
    )
      ? subscription?.cancelAtPeriodEnd &&
        subscription?.currentPeriodEnd &&
        parseISO(subscription?.currentPeriodEnd)
      : subscription?.status === 'past_due'
      ? subscription?.currentPeriodStart &&
        add(parseISO(subscription?.currentPeriodStart), {
          days: 15,
        })
      : null;

    const daysTillSubscriptionExpires =
      (subscriptionExpirationDate &&
        Math.floor(
          (subscriptionExpirationDate.getTime() - Date.now()) /
            (1000 * 60 * 60 * 24),
        )) ||
      0;

    const baseInfo = {
      subscriptionExpired:
        (subscription?.status && ['past_due'].includes(subscription.status)) ||
        false,
      subscriptionExpirationDate,
      daysTillSubscriptionExpires,
      subscriptionExpiringInDays:
        daysTillSubscriptionExpires > 0 && daysTillSubscriptionExpires < 7
          ? daysTillSubscriptionExpires
          : 0,
      clientsPurchased: subscription?.quantity || FREE_SUBSCRIPTION_APPS,
      clientsAdded: subscription?.quantity
        ? clientsPremiumViaPractice.length
        : subscription?.freeAppsUsed || 0,
    };

    const limitReached = baseInfo.clientsAdded / baseInfo.clientsPurchased >= 1;
    const limitApproaching =
      baseInfo.clientsAdded / baseInfo.clientsPurchased >= 0.8;

    return {
      ...baseInfo,
      limitReached,
      limitApproaching,
      clientsLeft: baseInfo.clientsPurchased - baseInfo.clientsAdded,
    };
  }, [
    stringifiedSubscrition,
    stringifiedMaterialised,
    clientsAdded,
    clientsPremiumViaPractice,
  ]);

  return res;
};
